import React, { useState } from 'react';
import { Button, Divider, List, Popover, Tag, Typography } from 'antd';
import { PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { history, useAccess } from '@umijs/max';
import { useCallbackQueue } from '@/contexts/CallbackQueueContext';
import CallbackQueueItem from '@/components/CallbackQueue/CallbackQueueItem';
import { useCallbackActions } from '@/components/CallbackQueue/useCallbackActions';
import styles from './index.less';

/** Nav-bar callback badge — reads from the shared CallbackQueueContext (one WebSocket, app-wide). */
const CallbackBadge: React.FC = () => {
  const access = useAccess();
  const { queue: allQueue, pendingCount, minePendingCount } = useCallbackQueue();
  // The context already returns only the oldest-waiting pending items (capped); this is the preview.
  const queue = allQueue
    .filter((i) => i.status === 'pending')
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  const [open, setOpen] = useState(false);
  // Row actions are shared with the site-wide banner (CallbackQueueBanner) — see useCallbackActions.
  const { updatingId, callBack, dismiss } = useCallbackActions({ onCallBackStarted: () => setOpen(false) });

  // ── access guard ─────────────────────────────────────────────────────────────
  if (!access?.canCallBoardMenu) return null;

  // ── popover content ──────────────────────────────────────────────────────────
  const content = (
    <div style={{ width: 380 }}>
      {/* header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Typography.Text strong>Pending Callbacks</Typography.Text>
        {pendingCount > 0 && <Tag color="red">{pendingCount} pending</Tag>}
      </div>

      {/* list */}
      {pendingCount === 0 ? (
        <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: '24px 0' }}>
          No pending callbacks
        </Typography.Text>
      ) : (
        <List
          dataSource={queue}
          style={{ maxHeight: 420, overflowY: 'auto' }}
          renderItem={(item) => (
            <List.Item style={{ padding: 0, display: 'block', border: 0 }}>
              <CallbackQueueItem item={item} updatingId={updatingId} onCallBack={callBack} onDismiss={dismiss} />
            </List.Item>
          )}
        />
      )}

      {pendingCount > queue.length && (
        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 8 }}>
          Showing {queue.length} of {pendingCount} — see all in Call Board
        </Typography.Text>
      )}

      <Divider style={{ margin: '8px 0' }} />
      <Button
        type="link"
        size="small"
        icon={<PhoneOutlined />}
        style={{ padding: 0 }}
        onClick={() => { setOpen(false); history.push('/tenant/communications/call_board'); }}
      >
        View all{pendingCount > 0 ? ` ${pendingCount}` : ''} in Call Board
      </Button>

      {/* The user's own private list (callbacks they promised at call wrap-up) — never
          part of the shared list above; lives on the Call Board "My Callbacks" tab. */}
      {minePendingCount > 0 && (
        <div style={{ marginTop: 4 }}>
          <Button
            type="link"
            size="small"
            icon={<UserOutlined />}
            style={{ padding: 0 }}
            onClick={() => { setOpen(false); history.push('/tenant/communications/call_board?queue=mine'); }}
          >
            {minePendingCount} {minePendingCount === 1 ? 'callback' : 'callbacks'} on My Callbacks
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <span
        className={styles.action}
        style={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '3px 10px',
          borderRadius: 4,
          background: pendingCount > 0 ? '#fff1f0' : '#f5f5f5',
          border: `1px solid ${pendingCount > 0 ? '#ffccc7' : '#d9d9d9'}`,
          color: pendingCount > 0 ? '#cf1322' : '#595959',
          fontWeight: 500,
          fontSize: 13,
          lineHeight: '20px',
          whiteSpace: 'nowrap',
        }}
      >
        <PhoneOutlined />
        {pendingCount > 99 ? '99+' : pendingCount} {pendingCount === 1 ? 'call' : 'calls'} in queue
        {minePendingCount > 0 && <span style={{ fontWeight: 400, opacity: 0.85 }}>· {minePendingCount} mine</span>}
      </span>
    </Popover>
  );
};

export default CallbackBadge;
